// Building blocks shared by the desktop Operations Hub sidebar and the phone Admin hub
// sheet. `dark` flips the palette: the sidebar is the command-side dark panel, the
// phone sheet stays on sheet-white.
import { useEffect } from 'preact/hooks';
import { deviceLabel, fmtAgo, localMondayISO } from '../../lib/dates';
import type { HubStats } from '../../state/hubstats';
import { fetchViewers, kickViewer, viewers } from '../../state/presence';
import { pickWeek, weeks } from '../../state/schedule';

export function Kpis({ s, dark }: { s: HubStats; dark?: boolean }) {
  const tile = dark ? 'bg-white/5' : 'bg-canvas';
  const label = dark ? 'text-white/50' : 'text-slate-500';
  const tiles = [
    { n: s.total, t: 'Calls', tone: dark ? 'text-white' : 'text-navy-900' },
    { n: s.done, t: 'Done', tone: 'text-emerald-500' },
    { n: s.missed, t: 'Missed', tone: 'text-red-500' },
    { n: s.open, t: 'Open', tone: 'text-brand-500' },
  ];
  return (
    <div class="grid grid-cols-4 gap-1.5" data-testid="hub-kpis">
      {tiles.map((k) => (
        <div key={k.t} class={`rounded-xl ${tile} px-1 py-2 text-center`}>
          <div class={`text-xl font-extrabold tabular-nums ${k.tone}`}>{k.n}</div>
          <div class={`text-[11px] font-semibold ${label}`}>{k.t}</div>
        </div>
      ))}
    </div>
  );
}

export function ProgressBar({ s, dark }: { s: HubStats; dark?: boolean }) {
  return (
    <div class="mt-2">
      <div class={`h-2 overflow-hidden rounded-full ${dark ? 'bg-white/10' : 'bg-slate-200'}`}>
        <div
          class="h-full rounded-full bg-brand-500 transition-[width] duration-500"
          style={{ width: `${s.pctDone}%` }}
        />
      </div>
      <div
        class={`mt-1 flex items-center text-xs font-semibold ${dark ? 'text-white/60' : 'text-slate-500'}`}
      >
        <span>
          {s.pctDone}% completed · {s.pctResolved}% resolved
        </span>
        {s.returns > 0 && <span class="ml-auto text-note-ink">{s.returns} need follow-up</span>}
      </div>
    </div>
  );
}

export function SectionTitle({ children, dark }: { children: string; dark?: boolean }) {
  return (
    <h3
      class={`mt-4 mb-1.5 text-[11px] font-bold tracking-widest uppercase ${dark ? 'text-white/40' : 'text-slate-400'}`}
    >
      {children}
    </h3>
  );
}

export function ByDay({ s, dark }: { s: HubStats; dark?: boolean }) {
  if (!s.byDay.length) return <Empty dark={dark}>No calls this week.</Empty>;
  return (
    <div class="space-y-1">
      {s.byDay.map((d) => (
        <div
          key={d.key}
          class={`flex items-center gap-2 text-sm ${dark ? 'text-white/80' : 'text-navy-900'}`}
        >
          <span class="w-20 truncate font-semibold">{d.label}</span>
          <span class="ml-auto text-xs font-semibold text-emerald-500 tabular-nums">
            {d.done} done
          </span>
          <span class="text-xs font-semibold text-red-500 tabular-nums">{d.missed} missed</span>
          <span
            class={`text-xs font-semibold tabular-nums ${dark ? 'text-white/50' : 'text-slate-500'}`}
          >
            {d.left} left
          </span>
        </div>
      ))}
    </div>
  );
}

export function ByTech({ s, dark }: { s: HubStats; dark?: boolean }) {
  const keys = s.anyAssigned ? Object.keys(s.byTech).sort() : Object.keys(s.byWho).sort();
  if (!keys.length) return <Empty dark={dark}>No completions yet.</Empty>;
  return (
    <div class="space-y-1" data-testid="hub-by-tech">
      {keys.map((t) => {
        if (s.anyAssigned) {
          const x = s.byTech[t]!;
          const pct = x.assigned ? Math.round((x.done / x.assigned) * 100) : 0;
          return (
            <div
              key={t}
              class={`flex items-center gap-2 text-sm ${dark ? 'text-white/80' : 'text-navy-900'}`}
            >
              <span class="min-w-0 flex-1 truncate font-semibold">{t}</span>
              <span class={`text-xs ${dark ? 'text-white/50' : 'text-slate-500'}`}>
                {x.done} done · {x.missed} missed · {x.assigned - x.done - x.missed} left
              </span>
              <span class="w-9 text-right text-xs font-bold text-brand-500 tabular-nums">
                {pct}%
              </span>
            </div>
          );
        }
        const y = s.byWho[t]!;
        return (
          <div
            key={t}
            class={`flex items-center gap-2 text-sm ${dark ? 'text-white/80' : 'text-navy-900'}`}
          >
            <span class="min-w-0 flex-1 truncate font-semibold">{t}</span>
            <span class={`text-xs ${dark ? 'text-white/50' : 'text-slate-500'}`}>
              {y.done} done · {y.missed} missed
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function Activity({ s, dark, limit }: { s: HubStats; dark?: boolean; limit?: number }) {
  const rows = limit ? s.activity.slice(0, limit) : s.activity;
  if (!rows.length) return <Empty dark={dark}>No activity yet.</Empty>;
  return (
    <div class="space-y-1.5" data-testid="hub-activity">
      {rows.map((r) => (
        <div key={`${r.id}-${r.at}`} class="flex items-baseline gap-2">
          <span
            class={`h-2 w-2 shrink-0 self-center rounded-full ${r.status === 'missed' ? 'bg-red-500' : 'bg-emerald-500'}`}
            aria-hidden="true"
          />
          <div class="min-w-0 flex-1">
            <p class={`truncate text-sm ${dark ? 'text-white/80' : 'text-navy-900'}`}>
              <span class="font-bold">{r.by || 'Someone'}</span>{' '}
              <span class={r.status === 'missed' ? 'text-red-500' : 'text-emerald-600'}>
                {r.status === 'missed' ? 'missed' : 'completed'}
              </span>
            </p>
            <p class={`truncate text-xs ${dark ? 'text-white/50' : 'text-slate-500'}`}>
              {(r.development ? `${r.development} — ` : '') + r.address}
            </p>
          </div>
          <span
            class={`shrink-0 text-xs whitespace-nowrap ${dark ? 'text-white/40' : 'text-slate-400'}`}
          >
            {fmtAgo(r.at)}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Archived weeks (strictly before the current local Monday), newest first. */
export function ArchiveList({ dark, onPick }: { dark?: boolean; onPick?: () => void }) {
  const monday = localMondayISO();
  const past = weeks.value
    .filter((w) => w.weekId < monday)
    .sort((a, b) => b.weekId.localeCompare(a.weekId));
  if (!past.length) return <Empty dark={dark}>No archived weeks yet.</Empty>;
  return (
    <div class="space-y-1" data-testid="hub-archive">
      {past.map((w) => (
        <button
          key={w.weekId}
          type="button"
          class={`tap flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm font-semibold ${dark ? 'text-white/80 hover:bg-white/5' : 'text-navy-900 hover:bg-canvas'}`}
          onClick={() => {
            pickWeek(w.weekId);
            onPick?.();
          }}
        >
          <span class="min-w-0 flex-1 truncate">{w.weekOf}</span>
          <span class={`text-xs tabular-nums ${dark ? 'text-white/40' : 'text-slate-400'}`}>
            {w.count}
          </span>
        </button>
      ))}
    </div>
  );
}

export function People({ dark }: { dark?: boolean }) {
  useEffect(() => {
    void fetchViewers().catch(() => {});
    const t = setInterval(() => void fetchViewers().catch(() => {}), 300_000);
    return () => clearInterval(t);
  }, []);
  const now = Date.now();
  const list = viewers.value;
  if (!list.length) return <Empty dark={dark}>No one has opened the app yet.</Empty>;
  return (
    <div class="space-y-1.5" data-testid="hub-people">
      {list.map((v) => {
        const seen = Date.parse(v.lastSeen) || 0;
        const online = seen > 0 && now - seen < 10 * 60 * 1000;
        return (
          <div key={v.id} class="flex items-center gap-2">
            <span
              class={`h-2 w-2 shrink-0 rounded-full ${online ? 'bg-emerald-500' : dark ? 'bg-white/20' : 'bg-slate-300'}`}
              aria-hidden="true"
            />
            <div class="min-w-0 flex-1">
              <p
                class={`truncate text-sm font-semibold ${dark ? 'text-white/80' : 'text-navy-900'}`}
              >
                {v.name || '(no name)'}
              </p>
              <p class={`truncate text-xs ${dark ? 'text-white/50' : 'text-slate-500'}`}>
                {deviceLabel(v.ua)} · {online ? 'online' : fmtAgo(v.lastSeen) || 'never'}
              </p>
            </div>
            <button
              class="mini-btn shrink-0"
              title="Remove this device — they re-enter their name next time"
              onClick={() => void kickViewer(v.id)}
            >
              Kick
            </button>
          </div>
        );
      })}
    </div>
  );
}

function Empty({ children, dark }: { children: string; dark?: boolean }) {
  return <p class={`py-2 text-sm ${dark ? 'text-white/40' : 'text-slate-400'}`}>{children}</p>;
}
